import { Prominences } from '@sede-enterprise/shell-ds-fabrics';
import {
  Option,
  Select,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import { getLatestAssetLimit } from 'pages/Availability/common/utils';
import { Asset, AssetLimit } from 'pages/Availability/types';

interface AssetSelectorProps {
  assets: Asset[];
  selectedAssets: string[]; // Array of assetSourceKeys
  onChange: (selectedAssets: string[]) => void;
  disabled?: boolean;
  testId?: string;
}

const formatAssetLimit = (limit: AssetLimit | null): string => {
  if (!limit) return '';
  return `${limit.maxExportPower} MW / ${limit.energyCapacity} MWh`;
};

export const AssetSelector = ({
  assets,
  selectedAssets,
  onChange,
  disabled = false,
  testId = 'assetSelector',
}: AssetSelectorProps) => {
  const assetOptions = assets.map((asset) => ({
    sourceKey: getSourceKey(asset),
    name: asset.name,
    limit: getLatestAssetLimit(asset.assetLimits),
  }));

  const handleChange = (values: string[]) => {
    const allAssetKeys = assetOptions.map((a) => a.sourceKey);
    const isAllSelected = values.includes('all');
    const wasAllSelected = selectedAssets.length === allAssetKeys.length;
    const individualAssets = values.filter((v) => v !== 'all');

    // "All assets" toggled on: select all
    if (isAllSelected && !wasAllSelected) {
      onChange(allAssetKeys);
      return;
    }

    // "All assets" toggled off when all were selected: clear all selections
    if (!isAllSelected && wasAllSelected) {
      onChange([]);
      return;
    }

    // Otherwise: use the individual asset selections
    onChange(individualAssets);
  };

  const displayValue =
    selectedAssets.length === assetOptions.length && assetOptions.length > 0
      ? ['all', ...selectedAssets]
      : selectedAssets;

  return (
    <Select
      data-testid={testId}
      placeholder="Select assets"
      mode="multiple"
      value={displayValue}
      onChange={handleChange}
      optionLabelProp="label"
      disabled={disabled}
      maxTagCount="responsive"
      dropdownAlign={{
        points: ['tl', 'bl'],
        overflow: { adjustX: true, adjustY: false },
      }}
    >
      <Option key="all" value="all" label="All assets">
        <Text>All assets</Text>
      </Option>
      {assetOptions.map((asset) => (
        <Option
          key={asset.sourceKey}
          value={asset.sourceKey}
          label={asset.name}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text>{asset.name}</Text>
            {asset.limit && (
              <Text size={Sizes.Small} prominence={Prominences.Subtle}>
                {formatAssetLimit(asset.limit)}
              </Text>
            )}
          </div>
        </Option>
      ))}
    </Select>
  );
};
